// Labels for the shared quote core (QuoteFields), in all three languages.
//
// It lives beside the component rather than inside either surface's pack
// because "the same form" has to mean the same WORDS too. The public RFQ page
// is English-only today and the supplier dashboard is trilingual; if each kept
// its own copy of "Price basis" they would drift, and a supplier who quoted
// through one door and then the other would be reading two different forms
// that happen to write the same columns.
//
// Golden rule 3: ar / en / zh everywhere supplier-facing.

export const QUOTE_STRINGS = {
  ar: {
    price: 'سعر الوحدة',
    currency: 'العملة',
    usdOnly: 'الأسعار بالدولار الأمريكي فقط — يراها التاجر بالريال تلقائياً.',
    cnyHint: '≈ ¥{amount} بسعر صرف معبر',
    priceBasis: 'أساس السعر',
    capTitle: 'إلى أين تقدر توصّل؟',
    capHint: 'نسألك مرة واحدة فقط، ونعبّي أساس السعر لك في كل عرض بعدها.',
    priceBases: {
      EXW: 'تسليم المصنع (EXW) — المشتري يرتّب كل شيء من باب مصنعك',
      FOB: 'تسليم الميناء (FOB) — أنت توصل للميناء والمشتري يدفع الشحن',
      CIF: 'شامل الشحن والتأمين (CIF) — أنت تغطي الشحن والتأمين حتى ميناء الوصول',
      DDP: 'تسليم باب المشتري (DDP) — أنت توصلها إلى عنوانه وتتحمّل الرسوم والتخليص',
    },
    // «بلد التصنيع» لا «المنشأ»: الثانية غامضة بما يكفي ليُكتب فيها ميناء الشحن.
    origin: 'بلد التصنيع (اختياري)',
    originPh: 'مثال: الصين',
    originHint: 'يُستعمل لشهادة المنشأ والتصنيف الجمركي لدى المشتري.',
    validFor: 'العرض صالح لمدة',
    validDays: { 15: '١٥ يوماً', 30: '٣٠ يوماً', 60: '٦٠ يوماً' },
    moq: 'الحد الأدنى للطلب',
    moqPh: 'مثال: ٥٠٠ قطعة',
    leadTime: 'مدة التجهيز (أيام)',
    leadTimePh: 'مثال: ٣٠',
    note: 'ملاحظات',
    notePh: 'أي شيء يهمّ المشتري — الخامات، التغليف، الشهادات.',
    sampleTitle: 'العيّنة',
    sampleAsk: {
      preferred: 'المشتري يفضّل عيّنة إن أمكنك توفيرها.',
      required: 'المشتري يشترط عيّنة قبل الطلب.',
    },
    sampleCanSupply: 'أستطيع توفير عيّنة',
    samplePrice: 'سعر العيّنة',
    samplePriceHint: 'بنفس عملة عرضك. اتركه فارغاً إن كانت مجانية.',
    sampleDays: 'العيّنة جاهزة خلال (أيام)',
    priceInvalid: 'أدخل سعر وحدة أكبر من صفر.',
    leadTimeInvalid: 'أدخل مدة التجهيز بالأيام كاملة.',
    priceBasisRequired: 'اختر أساس السعر — بدونه لا يمكن مقارنة سعرك بعروض أخرى.',
  },
  en: {
    price: 'Unit price',
    currency: 'Currency',
    usdOnly: 'Prices are in US dollars only — the trader sees SAR automatically.',
    cnyHint: '≈ ¥{amount} at Maabar\'s rate',
    priceBasis: 'Price basis',
    capTitle: 'Where can you deliver?',
    capHint: 'We ask once, then fill the price basis in for you on every quote after this.',
    priceBases: {
      EXW: 'EXW — buyer arranges everything from your factory door',
      FOB: 'FOB — you deliver to the port, buyer pays freight',
      CIF: 'CIF — you cover freight and insurance to the destination port',
      DDP: "DDP — you deliver to the buyer's address and carry duties and clearing",
    },
    // "Country of manufacture", not "Origin": the readers are Chinese
    // factories reading in translation, and "origin" is ambiguous enough to
    // get the shipping PORT typed into it.
    origin: 'Country of manufacture (optional)',
    originPh: 'e.g. China',
    originHint: 'Used for the buyer’s certificate of origin and customs classification.',
    validFor: 'Quote valid for',
    validDays: { 15: '15 days', 30: '30 days', 60: '60 days' },
    moq: 'Minimum order quantity',
    moqPh: 'e.g. 500 pieces',
    leadTime: 'Lead time (days)',
    leadTimePh: 'e.g. 30',
    note: 'Notes',
    notePh: 'Anything the buyer should know — materials, packaging, certifications.',
    sampleTitle: 'Sample',
    sampleAsk: {
      preferred: 'The buyer would like a sample if you can provide one.',
      required: 'The buyer requires a sample before ordering.',
    },
    sampleCanSupply: 'I can supply a sample',
    samplePrice: 'Sample price',
    samplePriceHint: 'In the same currency as your quote. Leave empty if free.',
    sampleDays: 'Sample ready in (days)',
    priceInvalid: 'Enter a unit price greater than zero.',
    leadTimeInvalid: 'Enter the lead time in whole days.',
    priceBasisRequired: 'Choose a price basis — without it your price cannot be compared with other quotes.',
  },
  zh: {
    price: '单价',
    currency: '币种',
    usdOnly: '报价仅限美元 — 买家会自动看到沙特里亚尔价格。',
    cnyHint: '≈ ¥{amount}（按平台汇率）',
    priceBasis: '价格条款',
    capTitle: '您可以送到哪里？',
    capHint: '只问一次，之后每次报价我们都会为您预填价格条款。',
    priceBases: {
      EXW: 'EXW 工厂交货 — 买家自您厂门起自行安排',
      FOB: 'FOB 离岸价 — 您负责送到港口，买家承担运费',
      CIF: 'CIF 到岸价 — 您承担至目的港的运费与保险',
      DDP: 'DDP 完税后交货 — 您送到买家地址，并承担关税和清关',
    },
    origin: '生产国（选填）',
    originPh: '例如：中国',
    originHint: '用于买家的原产地证书与海关归类。',
    validFor: '报价有效期',
    validDays: { 15: '15 天', 30: '30 天', 60: '60 天' },
    moq: '最小起订量',
    moqPh: '例如：500 件',
    leadTime: '交货周期（天）',
    leadTimePh: '例如：30',
    note: '备注',
    notePh: '买家需要了解的任何信息——材料、包装、认证。',
    sampleTitle: '样品',
    sampleAsk: {
      preferred: '如方便提供样品，买家希望获得。',
      required: '买家要求先看样品再下单。',
    },
    sampleCanSupply: '我可以提供样品',
    samplePrice: '样品价格',
    samplePriceHint: '与报价币种一致。免费请留空。',
    sampleDays: '样品交付天数',
    priceInvalid: '请输入大于零的单价。',
    leadTimeInvalid: '请输入整数天数的交货周期。',
    priceBasisRequired: '请选择价格条款——否则您的报价无法与其他报价比较。',
  },
};

export function quoteStrings(lang) {
  return QUOTE_STRINGS[lang] || QUOTE_STRINGS.en;
}
